import React from 'react';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { ListSubheader, makeStyles } from '@material-ui/core';

const LISTBOX_PADDING = 8; // px

const useStyles = makeStyles({
  root: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

/** Высота строки по умолчанию. Историческая: под неё сверстаны уже существующие диалоги. */
const DEFAULT_ITEM_SIZE = 80;

const buildListbox = (itemSize: number) =>
  React.forwardRef<HTMLDivElement>(function VirtualizedListbox(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  const classes = useStyles();

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref} className={classes.root}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

/**
 * Список с заданной высотой строки.
 *
 * Дефолтные 80 px рассчитаны на строку в две строчки текста, и для однострочных вариантов
 * (сотрудник, должность) выпадающий список из восьми таких строк занимает 640 px — он
 * перекрывает форму целиком. Размер задаётся снаружи, а не подбирается по содержимому:
 * react-window требует высоту до отрисовки, измерять её тут нечем.
 */
export const createVirtualizedListbox = (itemSize: number) =>
  buildListbox(itemSize) as React.ComponentType<React.HTMLAttributes<HTMLElement>>;

/** Компактная строка в одну строчку текста — размер пункта меню MUI. */
export const COMPACT_ITEM_SIZE = 40;

export default buildListbox(DEFAULT_ITEM_SIZE) as React.ComponentType<React.HTMLAttributes<HTMLElement>>;
